import type { CreditsData } from '@/api/auth';
import type { DepositBonusSettings } from '@/domain/bonuses';
import {
  parseSocketMessageRewards,
  type SocketMessageRewards,
} from '@/domain/rewards';
import type { FreeSpinsBonusGame } from './free-spins-notifications';

/** Parsed `PlayerMessage` payload from UsersAPI `PlayerSocket`. */
export interface PlayerSocketMessage {
  Type: string;
  Value?: unknown;
}

/** UsersAPI `SocketMessageFreeSpins` (enriched with Games). */
export interface FreeSpinsBonusPayload {
  ProviderName: string;
  Title: string | null;
  FreeRounds: number;
  StartTime?: string | null;
  EndTime?: string | null;
  Games: FreeSpinsBonusGame[];
}

export function parsePlayerSocketMessage(raw: unknown): PlayerSocketMessage | null {
  try {
    const parsed = typeof raw === 'string' ? (JSON.parse(raw) as unknown) : raw;
    if (!parsed || typeof parsed !== 'object') return null;
    const type = (parsed as { Type?: unknown }).Type;
    if (typeof type !== 'string' || !type) return null;
    return {
      Type: type,
      Value: (parsed as { Value?: unknown }).Value,
    };
  } catch {
    return null;
  }
}

function asCreditsData(value: unknown): CreditsData | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  // At least one known credit field must be present.
  const keys = [
    'WithdrawableCredits',
    'RestrictedCredits',
    'BonusCredits',
    'CasinoBonusCredits',
    'ExternalSportsbookBonusCredits',
  ] as const;
  if (!keys.some((key) => typeof record[key] === 'number')) return null;
  return value as CreditsData;
}

/** Credits may be the message value itself, or nested under `Value.Credits`. */
export function creditsFromPlayerMessage(message: PlayerSocketMessage): CreditsData | null {
  const direct = asCreditsData(message.Value);
  if (direct) return direct;
  if (message.Value && typeof message.Value === 'object') {
    return asCreditsData((message.Value as { Credits?: unknown }).Credits);
  }
  return null;
}

/** Reward add/delete payload from Type `Reward`. */
export function rewardsFromPlayerMessage(
  message: PlayerSocketMessage,
): SocketMessageRewards | null {
  return parseSocketMessageRewards(message.Value);
}

/** Deposit bonus settings from Type `DepositBonusSettings`. */
export function depositBonusSettingsFromPlayerMessage(
  message: PlayerSocketMessage,
): DepositBonusSettings | null {
  const value = message.Value;
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (typeof record.Type !== 'number' && typeof record.Type !== 'string') return null;
  return value as DepositBonusSettings;
}

function parseFreeSpinsGame(raw: unknown): FreeSpinsBonusGame | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const gameTag = typeof record.GameTag === 'string' ? record.GameTag.trim() : '';
  const name = typeof record.Name === 'string' ? record.Name.trim() : '';
  const vendorName =
    typeof record.VendorName === 'string' ? record.VendorName.trim() : '';
  if (!gameTag || !name) return null;
  return { GameTag: gameTag, Name: name, VendorName: vendorName };
}

/** Free spins grant from Type `FreeSpinsBonus`. */
export function freeSpinsBonusFromPlayerMessage(
  message: PlayerSocketMessage,
): FreeSpinsBonusPayload | null {
  const value = message.Value;
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const freeRounds = Number(record.FreeRounds);
  if (!Number.isFinite(freeRounds) || freeRounds <= 0) return null;

  const gamesRaw = record.Games;
  const games = Array.isArray(gamesRaw)
    ? gamesRaw.map(parseFreeSpinsGame).filter((game): game is FreeSpinsBonusGame => game != null)
    : [];

  return {
    ProviderName:
      typeof record.ProviderName === 'string' ? record.ProviderName.trim() : '',
    Title: typeof record.Title === 'string' ? record.Title.trim() || null : null,
    FreeRounds: freeRounds,
    StartTime: typeof record.StartTime === 'string' ? record.StartTime : null,
    EndTime: typeof record.EndTime === 'string' ? record.EndTime : null,
    Games: games,
  };
}
